import './SpendLandscapePage.scss'
import React, {useEffect, useRef, useState} from "react";
import {observer} from "mobx-react-lite";
import {useStores} from "~/stores";
import {
    Alert,
    AlertTitle,
    Box,
    Button,
    Card,
    CardContent,
    Divider,
    FormControl,
    FormControlLabel,
    FormGroup,
    Grid,
    InputLabel,
    Switch,
    Tooltip,
    Typography
} from "@mui/material";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import {routes} from "~/routing/routes";
import {generatePath, useNavigate, useSearchParams} from "react-router";
import {runInAction} from "mobx";
import {TreeMap} from "~/components/visualization/tree-map/TreeMap";
import {BubbleMap} from "~/components/visualization/bubble-map/BubbleMap";
import {LineChart} from "~/components/visualization/line-chart/LineChart";
import {DonutChart} from "~/components/visualization/donut-chart/DonutChart";
import {AdvancedBarChart} from "~/components/visualization/bar-chart/AdvancedBarChart";
import {MultiSelection} from "~/components/visualization/api-dropdown-filter/ApiDropdownTypes";
import {MainPageFacade} from "~/components/main/page/MainPageFacade.tsx";
import {CARD_CONFIG} from "~/components/dashboard/DashboardCardConfigs.tsx";
import LoadingBarStore from "~/stores/LoadingBarStore.tsx";
import {decodeFilterFromUrl, FILTER_URL_PARAM} from "~/stores/managers/spend-landscape/spendLandscapeFilters";
import {SpendLandscapeFilterBar} from "./filters/SpendLandscapeFilterBar";
import {SpendLandscapeKpis} from "./components/SpendLandscapeKpis";
import {SupplierLocationsCard} from "./components/SupplierLocationsCard";
import {SpendByCategoryCard} from "./components/SpendByCategoryCard";
import {SpendBreakdownCard} from "./components/SpendBreakdownCard";
import {SupplierSpendCard} from "./components/SupplierSpendCard";
import {ByParentSupplierSwitch} from "./components/ByParentSupplierSwitch";
import {CategoryProfileBar} from "./components/CategoryProfileBar";
import {SpendLandscapeTables} from "./tables/SpendLandscapeTables";

export const SpendLandscapePage: React.FC = observer(() => {
    const {
        p,
        bagStore,
        businessCaseGeneratorStore,
        synergyStore,
    } = useStores();
    const navigate = useNavigate();
    const databagId = bagStore.bagId;
    const filterStore = bagStore.advanced.filterStore;
    // TODO CAT-2195: This would be better configured by a field in databag or synergy model,
    //  that would specify if user is allowed to split the advanced dashboard

    const [isLoading, setIsLoading] = useState(false);

    // Check for a requested databag id change
    useEffect(() => {
        bagStore.advanced.setDatabagId(databagId);
    }, [databagId, bagStore.advanced])

    // Filters from a shared link win over the remembered filters; the link follows the applied filters
    const [searchParams, setSearchParams] = useSearchParams();
    const linkFilter = useRef(searchParams.get(FILTER_URL_PARAM));
    useEffect(() => {
        const filter = decodeFilterFromUrl(linkFilter.current);
        if (filter) filterStore.setUrlFilter(filter);
    }, [filterStore]);
    const urlValue = filterStore.urlValue;
    const filtersLoaded = filterStore.loaded;
    useEffect(() => {
        if (!filtersLoaded) return;
        setSearchParams(prev => {
            if ((prev.get(FILTER_URL_PARAM) ?? null) === urlValue) return prev;
            const next = new URLSearchParams(prev);
            if (urlValue) next.set(FILTER_URL_PARAM, urlValue);
            else next.delete(FILTER_URL_PARAM);
            return next;
        }, {replace: true});
    }, [urlValue, filtersLoaded, setSearchParams]);

    const isReady = Boolean(bagStore.advanced.kpi ?? false) && !bagStore.advanced.isLoading;
    useEffect(() => {
        if (!isReady) {
            LoadingBarStore.start(true);
        } else {
            LoadingBarStore.setComplete()
        }
    }, [isReady])

    useEffect(() => {
        runInAction(() => {
            businessCaseGeneratorStore.hasLoaded = false;
            businessCaseGeneratorStore.risksHaveLoaded = false;
        })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // The parent supplier switch only makes sense when the dataset has supplier normalization results
    const hasParentSuppliers = Boolean(bagStore.bag?.has_supplier_normalization_results);

    const _Parent_Supplier = p.p.parentSupplierAltName ?? 'Parent Supplier';
    const _parent_supplier = _Parent_Supplier.toLowerCase();

    const parentSupplierSwitch = hasParentSuppliers
        ? <ByParentSupplierSwitch label={`By ${_parent_supplier}`}/>
        : undefined;

    // Synergy split view (unchanged)
    const enableParentSupplierSwitch = Boolean((bagStore.advanced.filters.group_by_parent_supplier ?? null) !== null);
    const JustParentSupplierSwitchComponent = <FormGroup className="advanced-dashboard-switch">
        <FormControlLabel
            control={<Switch
                checked={bagStore.advanced.groupParentSuppliersFilter}
                onChange={(_e, checked: boolean) => bagStore.advanced.groupParentSuppliersFilter = checked}
            />}
            label={<Typography variant="caption" className="">
                {_Parent_Supplier}
            </Typography>}
        />
    </FormGroup>

    return (
        <MainPageFacade
            // Mithra 3.0 page heading, as on the Category Profile pages
            title={<Typography variant="h5" component="h1" sx={{fontWeight: 600}}>{CARD_CONFIG['adv-dashboard'].title}</Typography>}
            actionBarStart={<>
                {p.opportunitySplitAllowed && synergyStore.isSynergyDatabag &&
                    <Tooltip title={bagStore.advanced.synergySplited ?
                        'These are comparison between sources of synergy file, you can combine them for overview.' :
                        'This is overview of synergy file, you can split it to compare sources.'}>
                        <span>
                            <Button variant="outlined" className="mx-4" onClick={() => {
                            bagStore.advanced.setSynergySplited(!bagStore.advanced.synergySplited);
                        }}>
                                {bagStore.advanced.synergySplited ? 'Combine' : 'Split'}
                            </Button>
                        </span>
                    </Tooltip>
                }
            </>}
            className="advanced-dashboard-page spend-landscape-page">

            <Box className="spend-landscape-content">
                <SpendLandscapeKpis/>
                {bagStore.advanced_job.already_running &&
                    <Alert severity="warning" className="advanced-dashboard-alert">
                        <AlertTitle>
                            A data refresh is still being processed
                        </AlertTitle>
                        This page might not be up to date.
                    </Alert>}

                <SpendLandscapeFilterBar/>

                {p.p.demoFakeBusinessCaseGenerator && MultiSelection.isSet(bagStore.advanced.filters.category_l1) && (bagStore.advanced.singleSelectedCategory?.length ?? 0) > 1 && (
                    <Alert
                        severity="info"
                        action={
                            <>
                                <div style={{display: 'flex', alignItems: 'baseline'}}>
                                    <FormControl variant="standard" style={{width: '20em', marginRight: '1em'}}>
                                        <InputLabel id="business-case-dropdown-label">Select Strategy</InputLabel>
                                        <Select
                                            labelId="business-case-dropdown-label"
                                            id="business-case-dropdown"
                                            label="Select Strategy"
                                            onChange={(event) => {
                                                runInAction(() => {
                                                    businessCaseGeneratorStore.selectedStrategy = event.target.value;
                                                });
                                            }}
                                        >
                                            <MenuItem value={1}>Demand / supplier consolidation</MenuItem>
                                            <MenuItem value={2}>Time to market ( TTM ) optimization</MenuItem>
                                            <MenuItem value={3}>Tail spend reduction</MenuItem>
                                            <MenuItem value={4}>Optimize cash flow</MenuItem>
                                            <MenuItem value={5}>Price variation optimization</MenuItem>
                                            <MenuItem value={6}>Multi - source strategy</MenuItem>
                                        </Select>
                                    </FormControl>
                                    <Button
                                        style={{width: '15em'}}
                                        disabled={!businessCaseGeneratorStore.selectedStrategy || isLoading}
                                        onClick={() => {
                                            setIsLoading(true);
                                            // Simulate a delay for 1.8 seconds and perform your actions
                                            setTimeout(() => {
                                                runInAction(() => {
                                                    businessCaseGeneratorStore.hasLoaded = false;
                                                    businessCaseGeneratorStore.pageController.navigateToPage('review-opportunities');
                                                });
                                                navigate(generatePath(routes.demo_business_case_report, {id: bagStore.bagId}));
                                                setIsLoading(false);
                                            }, 1800);
                                        }}
                                    >
                                        {isLoading ? 'Generating...' : 'Generate Business Case'}
                                    </Button>
                                </div>
                            </>
                        }
                    >
                        {/*<AlertTitle>You can use Gen AI to build your Business case</AlertTitle>*/}
                        {/*Based on the data in this dashboard, you can generate a business case.*/}
                        <AlertTitle>Select scope and defined category strategy to build your business
                            case</AlertTitle>
                        Based on this selection, you can use Mithra Ai assistant to kick start a business case
                    </Alert>
                )}

                {!bagStore.advanced.synergySplited && <Box className="spend-landscape-charts">
                    <SupplierLocationsCard/>
                    <SpendByCategoryCard/>
                    <SpendBreakdownCard toggle={parentSupplierSwitch}/>
                    <SupplierSpendCard toggle={parentSupplierSwitch}/>
                </Box>}

                {bagStore.advanced.synergySplited && <Grid container>
                    {bagStore.synergy.srcDatabagIds?.map((databagId, index) =>
                        <Grid key={index} size={12 / (bagStore.synergy.srcDatabagIds?.length ?? 1)}>
                            <Typography variant="h5" component="p"
                                        style={{textAlign: 'center', wordBreak: 'break-word'}}>
                                {bagStore.getBagName(databagId)}
                            </Typography>
                        </Grid>
                    )}
                    {/*TODO CAT-2195: The limitation here is that number of sources must be less than or equal to 4*/}
                    {bagStore.advanced.treeDataSplited.map((atd, index) =>
                        <Grid
                            key={index}
                            className="advanced-dashboard-chart"
                            size={12 / bagStore.advanced.treeDataSplited.length}>
                            <TreeMap
                                data={atd}
                                selectedCategory={bagStore.advanced.singleSelectedCategory ?? null}
                                categoryFilterChanged={bagStore.advanced.onTreeMapCategoryFilterChanged}
                            />
                        </Grid>)}
                    {bagStore.advanced.spendDonutDataSplited.map((donutChartSupplierSpendData, index) =>
                        <Grid
                            key={index}
                            className="advanced-dashboard-chart"
                            size={12 / bagStore.advanced.spendDonutDataSplited.length}>
                            <Card className="advanced-dashboard-chart-card">
                                <CardContent>
                                    <Typography variant={'h6'} className={'advanced-dashboard-title'}>
                                        Spend breakdown
                                        {enableParentSupplierSwitch && JustParentSupplierSwitchComponent}
                                    </Typography>
                                    <Typography sx={{fontSize: 14}} color="text secondary" gutterBottom
                                                className={'advanced-dashboard-subtitle'}>
                                        Spend distribution by top suppliers
                                    </Typography>
                                    <Divider/>
                                    <DonutChart
                                        data={donutChartSupplierSpendData}
                                        loading={bagStore.advanced.advancedSpendDonutDataSplitedRMs[index].busy}
                                        selection={bagStore.advanced.groupParentSuppliersFilter ? bagStore.advanced.filters.sp_ids : bagStore.advanced.filters.s_ids}
                                        onSelectionChange={bagStore.advanced.groupParentSuppliersFilter ? bagStore.advanced.parentSupplierFilterChanged : bagStore.advanced.supplierFilterChanged}
                                    />
                                </CardContent>
                            </Card>
                        </Grid>)}
                    {bagStore.advanced.spendConcentrationDataSplited.map((asd, index) =>
                        <Grid
                            key={index}
                            className="advanced-dashboard-chart"
                            size={12 / bagStore.advanced.spendDonutDataSplited.length}>
                            <Card className="advanced-dashboard-chart-card">
                                <CardContent>
                                    <Typography variant={'h6'} className={'advanced-dashboard-title'}>
                                        Supplier spend
                                        {enableParentSupplierSwitch && JustParentSupplierSwitchComponent}
                                    </Typography>
                                    <Typography sx={{fontSize: 14}} color="text secondary" gutterBottom
                                                className={'advanced-dashboard-subtitle'}>
                                        Total spend distribution per supplier
                                    </Typography>
                                    <Divider/>
                                    <AdvancedBarChart
                                        dataWrapper={asd}
                                        columnBreakDown={bagStore.advanced.spendDonutDataSplited.length}
                                        loading={bagStore.advanced.advancedSpendConcentrationDataSplitedRMs[index].busy}
                                        selection={bagStore.advanced.groupParentSuppliersFilter ? bagStore.advanced.filters.sp_ids : bagStore.advanced.filters.s_ids}
                                        onSelectionChange={bagStore.advanced.groupParentSuppliersFilter ? bagStore.advanced.parentSupplierFilterChanged : bagStore.advanced.supplierFilterChanged}
                                    />
                                </CardContent>
                            </Card>
                        </Grid>)}
                    <Grid className="advanced-dashboard-chart full-width-bubble-map" size={12}>
                        {/*TODO: CAT-2894: Also make a Region based geoMap*/}
                        <BubbleMap
                            data={bagStore.advanced.geoBubbleDataSplited}
                            selectedIds={bagStore.advanced.filters.country}
                            onSelectedChanged={bagStore.advanced.hasGeoRegionFilter ? undefined : bagStore.advanced.countryFilterChanged}
                        />
                    </Grid>
                </Grid>}

                <SpendLandscapeTables afterOpportunities={bagStore.configStore.customerConfig?.get('ADV_DASH_GRAPHS') &&
                    <section className="advanced-line-graph-container">
                        <Card>
                            <CardContent>
                                <Typography variant="h6">
                                    Spend per supplier
                                </Typography>
                                <Typography sx={{fontSize: 14}} color="text.secondary" gutterBottom
                                            className="advanced-dashboard-subtitle">
                                    Analyzing trends in various categories
                                </Typography>
                                <Divider className="my-1"/>
                                <Box mt={2}>
                                    <LineChart dataWrapper={bagStore.advanced.spendPerSupplierData}/>
                                </Box>
                            </CardContent>
                        </Card>
                        <Card>
                            <CardContent>
                                <Typography variant="h6">
                                    Spend per level one category
                                </Typography>
                                <Typography sx={{fontSize: 14}} color="text.secondary" gutterBottom
                                            className={'advanced-dashboard-subtitle'}>
                                    Analyzing trends in specific levels
                                </Typography>
                                <Divider className="my-1"/>
                                <Box mt={2}>
                                    <LineChart dataWrapper={bagStore.advanced.spendPerActiveL1Data}/>
                                </Box>
                            </CardContent>
                        </Card>
                    </section>
                }/>

                <CategoryProfileBar/>
            </Box>
        </MainPageFacade>
    );
});
